import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Megaphone, Plus, PaperPlaneTilt, PencilSimple, Trash, ChartLineUp,
  CheckCircle, UsersThree, DeviceMobile, Cursor,
} from "@phosphor-icons/react";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";

const TIERS = ["bronze", "silver", "gold", "king"];
const STATUSES = [
  { id: "em_dia", label: "Em dia" },
  { id: "proximo", label: "Próximo do vencimento" },
  { id: "vencido", label: "Vencidos" },
];

const emptyForm = () => ({
  id: null, name: "", title: "", message: "",
  all_clients: false, tiers: {}, statuses: {}, active: true,
});

const Campaigns = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [pushCfg, setPushCfg] = useState({ enabled: false });

  const load = async () => {
    try {
      const [{ data }, { data: cfg }] = await Promise.all([
        api.get("/admin/campaigns"),
        api.get("/push/config").catch(() => ({ data: { enabled: false } })),
      ]);
      setItems(data); setPushCfg(cfg);
    } catch { toast.error("Erro ao carregar campanhas"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(emptyForm()); setOpen(true); };
  const openEdit = (c) => {
    setForm({
      id: c.id,
      name: c.name || "",
      title: c.title || "",
      message: c.message || "",
      all_clients: !!c.all_clients,
      tiers: Object.fromEntries((c.tiers || []).map((t) => [t, true])),
      statuses: Object.fromEntries((c.statuses || []).map((s) => [s, true])),
      active: c.active !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.title || !form.message) {
      return toast.error("Preencha nome, título e mensagem");
    }
    const tiers = Object.keys(form.tiers).filter((k) => form.tiers[k]);
    const statuses = Object.keys(form.statuses).filter((k) => form.statuses[k]);
    if (!form.all_clients && !tiers.length && !statuses.length) {
      return toast.error("Defina um segmento ou marque 'Todos os clientes'");
    }
    const payload = {
      name: form.name, title: form.title, message: form.message,
      all_clients: !!form.all_clients,
      tiers: tiers.length ? tiers : null,
      statuses: statuses.length ? statuses : null,
      active: !!form.active,
    };
    setBusy(true);
    try {
      if (form.id) await api.put(`/admin/campaigns/${form.id}`, payload);
      else await api.post("/admin/campaigns", payload);
      toast.success(form.id ? "Campanha atualizada" : "Campanha criada");
      setOpen(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Excluir a campanha "${c.name}"?`)) return;
    try { await api.delete(`/admin/campaigns/${c.id}`); toast.success("Excluída"); load(); }
    catch { toast.error("Erro ao excluir"); }
  };

  const sendNow = async (c) => {
    if (!window.confirm(`Enviar "${c.name}" agora?`)) return;
    setSendingId(c.id);
    try {
      const { data } = await api.post(`/admin/campaigns/${c.id}/send`);
      if (data?.push?.ok) {
        toast.success(`Push enviado: ${data.push.recipients}/${data.targets} dispositivos`);
      } else {
        toast.warning(`${data.targets} alvos · ${data.players_reached} com dispositivo · ${data?.push?.error || "OK"}`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar");
    } finally { setSendingId(null); }
  };

  const refreshMetrics = async (c) => {
    try {
      await api.get(`/admin/campaigns/${c.id}/metrics`);
      toast.success("Métricas atualizadas");
      load();
    } catch { toast.error("Sem métricas disponíveis"); }
  };

  const segmentLabel = (c) => {
    if (c.all_clients) return "Todos os clientes";
    const parts = [];
    if (c.tiers?.length) parts.push(c.tiers.map((t) => t.toUpperCase()).join(", "));
    if (c.statuses?.length) parts.push(c.statuses.join(", ").replace("em_dia", "Em dia").replace("proximo", "Próximo").replace("vencido", "Vencidos"));
    return parts.join(" · ") || "Nenhum";
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-semibold">Comunicação</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Campanhas</h1>
          <p className="text-sm text-zinc-500 mt-1">Templates de push prontos para reenviar com 1 clique.</p>
        </div>
        <Button onClick={openCreate} data-testid="campaign-new-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="mr-2" size={16}/> Nova campanha
        </Button>
      </div>

      {!pushCfg.enabled && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
          OneSignal não está configurado — campanhas serão salvas mas não enviarão push até configurar o servidor.
        </div>
      )}

      {loading ? (
        <div className="text-zinc-500">Carregando...</div>
      ) : items.length === 0 ? (
        <Card className="border-zinc-200 p-12 text-center card-soft">
          <Megaphone size={36} weight="duotone" className="mx-auto mb-3 text-zinc-400"/>
          <div className="font-display text-lg font-semibold">Nenhuma campanha ainda</div>
          <div className="text-sm text-zinc-500 mt-1 mb-4">Crie templates como "Desconto VIP Gold" ou "Vencimento atrasado" e reenvie quando quiser.</div>
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus className="mr-2" size={16}/> Criar primeira campanha
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="campaign-grid">
          {items.map((c) => (
            <Card key={c.id} className={`border p-5 card-soft ${c.active ? "border-zinc-200 bg-white" : "border-zinc-200 bg-zinc-50 opacity-70"}`} data-testid={`campaign-card-${c.id}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-700 grid place-items-center">
                    <Megaphone size={18} weight="duotone"/>
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-semibold truncate" title={c.name}>{c.name}</div>
                    <div className="text-[10px] tracking-widest uppercase text-zinc-500">{c.active ? "Ativa" : "Inativa"}</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(c)} title="Editar" className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded" data-testid={`campaign-edit-${c.id}`}>
                    <PencilSimple size={16}/>
                  </button>
                  <button onClick={() => remove(c)} title="Excluir" className="p-1.5 text-zinc-500 hover:text-rose-700 hover:bg-rose-50 rounded" data-testid={`campaign-del-${c.id}`}>
                    <Trash size={16}/>
                  </button>
                </div>
              </div>

              <div className="text-sm font-medium text-zinc-900 line-clamp-1">{c.title}</div>
              <div className="text-xs text-zinc-600 mt-1 line-clamp-2 min-h-[2.5rem]">{c.message}</div>

              <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-700 text-[11px] font-medium">
                <UsersThree size={12} weight="fill"/> {segmentLabel(c)}
              </div>

              <div className="grid grid-cols-3 gap-2 mt-4 text-center border-t border-zinc-100 pt-3">
                <Metric icon={PaperPlaneTilt} label="Enviados" value={c.sent_count || 0}/>
                <Metric icon={DeviceMobile} label="Entregues" value={c.total_delivered || 0}/>
                <Metric icon={Cursor} label="Cliques" value={c.total_clicked || 0}/>
              </div>

              {c.last_sent_at && (
                <div className="text-[11px] text-zinc-500 mt-2 text-center">Último envio: {formatDate(c.last_sent_at)}</div>
              )}

              <div className="grid grid-cols-2 gap-2 mt-3">
                <Button
                  onClick={() => sendNow(c)}
                  disabled={sendingId === c.id || !c.active}
                  data-testid={`campaign-send-${c.id}`}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {sendingId === c.id ? "Enviando..." : (<><PaperPlaneTilt className="mr-1" size={14}/> Enviar agora</>)}
                </Button>
                <Button
                  onClick={() => refreshMetrics(c)}
                  variant="outline"
                  data-testid={`campaign-metrics-${c.id}`}
                  disabled={!c.last_notification_id}
                >
                  <ChartLineUp className="mr-1" size={14}/> Métricas
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone size={20} weight="duotone" className="text-blue-600"/>
              {form.id ? "Editar campanha" : "Nova campanha"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">Nome interno</Label>
              <Input
                className="mt-1.5"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder='Ex: "Desconto VIP Gold"'
                data-testid="campaign-form-name"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">Título do push</Label>
                <Input className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="campaign-form-title"/>
              </div>
              <div className="flex items-end justify-end">
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="campaign-form-active"/>
                  Campanha ativa
                </label>
              </div>
            </div>
            <div>
              <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">Mensagem</Label>
              <Textarea className="mt-1.5" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="campaign-form-message"/>
            </div>

            <label className="flex items-center justify-between border border-zinc-200 rounded-lg px-3 py-2.5">
              <div className="text-sm font-medium">Enviar para todos os clientes</div>
              <Switch
                checked={form.all_clients}
                onCheckedChange={(v) => setForm({ ...form, all_clients: v, tiers: v ? {} : form.tiers, statuses: v ? {} : form.statuses })}
                data-testid="campaign-form-all"
              />
            </label>

            <div className={form.all_clients ? "opacity-40 pointer-events-none" : ""}>
              <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">Por nível de fidelidade</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                {TIERS.map((t) => (
                  <label key={t} className="flex items-center gap-2 px-3 py-2 border border-zinc-200 rounded-lg cursor-pointer hover:bg-zinc-50">
                    <Checkbox
                      checked={!!form.tiers[t]}
                      onCheckedChange={(v) => setForm({ ...form, tiers: { ...form.tiers, [t]: !!v } })}
                      data-testid={`campaign-form-tier-${t}`}
                    />
                    <span className="text-sm capitalize">{t}</span>
                  </label>
                ))}
              </div>
              <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold mt-4 block">Por status de pagamento</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                {STATUSES.map((st) => (
                  <label key={st.id} className="flex items-center gap-2 px-3 py-2 border border-zinc-200 rounded-lg cursor-pointer hover:bg-zinc-50">
                    <Checkbox
                      checked={!!form.statuses[st.id]}
                      onCheckedChange={(v) => setForm({ ...form, statuses: { ...form.statuses, [st.id]: !!v } })}
                      data-testid={`campaign-form-status-${st.id}`}
                    />
                    <span className="text-sm">{st.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={busy} data-testid="campaign-form-save" className="bg-blue-600 hover:bg-blue-700 text-white">
              {busy ? "Salvando..." : (form.id ? "Atualizar" : "Criar campanha")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Metric = ({ icon: Icon, label, value }) => (
  <div>
    <Icon size={14} weight="duotone" className="text-zinc-500 mx-auto"/>
    <div className="font-display font-semibold text-base mt-0.5">{value}</div>
    <div className="text-[10px] tracking-widest uppercase text-zinc-500">{label}</div>
  </div>
);

export default Campaigns;
